import React, { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router';
import Button from 'components/inputs/Button';
import ButtonNew from 'components/inputs/ButtonNew';
import { finishEdit } from 'actions/nodeSettings';
import { saveMatrix, resetMatrix } from 'actions/matrices';
import classNames from 'classnames';
import * as urls from 'constants/urls';
import './Overrides.scss';
import NodePill from './NodePill';

const formatOverrideLabel = (override, matrix, experiment) => {
  const labelMapping = {
    abundance: 'Abundance',
    abundanceExaggeration: 'Abundance Exaggeration',
    activationFunction: 'Activation function',
    fixed: 'Node type',
    imageUrl: 'Image',
    mean: 'Mean',
    membership: 'Membership functions',
    name: 'Name',
    populationUnit: 'Population unit',
    trophicLevel: 'Trophic level',
    connection: 'Connection',
  };

  return labelMapping[override.type] || override.type;
};

const formatOverride = (override, matrix, experiment) => {
  switch (override.type) {
    case 'connection':
      return {
        ...override,
        connectionDescription:
          '(' +
          (
            matrix.nodes[override.connection.from] ||
            experiment.nodes[override.connection.from]
          ).name +
          ' to ' +
          (
            matrix.nodes[override.connection.to] ||
            experiment.nodes[override.connection.to]
          ).name +
          ')',
      };
    case 'fixed':
      return {
        to: override.to ? 'Input' : 'Output',
        from: override.from ? 'Input' : 'Output',
      };
    case 'imageUrl':
      return {
        to: <img alt="" src={override.to} />,
        from: <img alt="" src={override.from} />,
      };
    default:
      return override;
  }
};

const OverridesListElement = ({
  children,
  editable,
  node,
  overrides,
  theme = 'light',
}) => {
  const history = useHistory();
  const dispatch = useDispatch();

  const isNew = overrides.length > 0 ? overrides[0].type === 'new' : false;
  const isDel = overrides.length > 0 ? overrides[0].type === 'removed' : false;

  return (
    <React.Fragment>
      <li
        className={classNames('OverridesListElement', theme, {
          deleted: isDel,
        })}
      >
        <div className="OverridesListElement-title">
          <NodePill node={node} backgroundColor="transparent" />
          {isNew ? (
            <div className={classNames('OverridesListElement-new', theme)}>
              New Node
            </div>
          ) : null}
        </div>
        <div className="OverridesListElement-content">
          <div className="OverridesListElement-element">
            <div className="OverridesListElement-details"></div>
            <div className="OverridesListElement-override-details">
              {editable && isNew ? (
                <Button
                  className="modifications-button"
                  iconLeft="trash"
                  label="Remove New Node"
                  onClick={() => {
                    dispatch(finishEdit(overrides[0].revert));
                    dispatch(saveMatrix(overrides[0].revert.id));
                  }}
                />
              ) : editable && isDel ? (
                <Button
                  className="modifications-button"
                  iconLeft="reseticon"
                  label="Restore Deleted Node"
                  onClick={() => {
                    dispatch(finishEdit(overrides[0].revert));
                    dispatch(saveMatrix(overrides[0].revert.id));
                    history.push(urls.experiment(overrides[0].revert));
                  }}
                />
              ) : null}
            </div>
          </div>
          {!isNew && !isDel && overrides.length > 0 ? (
            <div className="OverridesListElement-overrides">{children}</div>
          ) : null}
        </div>
      </li>
    </React.Fragment>
  );
};

const OverrideElement = ({
  editable,
  override,
  experiment,
  matrix,
  theme = 'light',
}) => {
  const dispatch = useDispatch();

  return (
    <div className={classNames('OverrideElement', theme)}>
      <div className="OverrideElement-property">
        {formatOverrideLabel(override, matrix, experiment)}
      </div>
      <div className="OverrideElement-breadcrumbs">
        {override.type !== 'new' && override.type !== 'removed' ? (
          <div className="OverrideElement-description">
            <div className="OverrideElement-connection">
              {
                formatOverride(override, matrix, experiment)
                  .connectionDescription
              }
            </div>
            <div className="OverrideElement-fromToArrow">
              <div className="OverrideElement-from">
                {formatOverride(override, matrix, experiment).from}
              </div>
              <div className="OverrideElement-arrow">→</div>
              <div className="OverrideElement-to">
                {formatOverride(override, matrix, experiment).to}
              </div>
            </div>
            <div className="OverrideElement-units">
              {formatOverride(override, matrix, experiment).unit}
            </div>
          </div>
        ) : null}
      </div>
      <div className="OverrideElement-actions">
        {editable ? (
          <ButtonNew
            buttonClasses="icon-button-extra-small"
            icon="trash"
            onClick={() => {
              dispatch(resetMatrix(true));

              setTimeout(() => {
                dispatch(finishEdit(override.revert));
                dispatch(saveMatrix(experiment.id));
              }, 300);
            }}
          />
        ) : null}
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------------------------------------------

const OverrideElementTrophicLevel = ({ override, theme = 'light' }) => {
  const isNew = override.type === 'add';
  const isDel = override.type === 'delete';
  const isReorder = override.type === 'reorder';

  return (
    <React.Fragment>
      <li className={classNames('OverridesListElement trophic-level', theme)}>
        <div className="OverridesListElement-title">
          {isNew || isDel ? (
            <NodePill
              node={{
                name: override?.trophicLevel,
                trophicLevel: override?.trophicLevel,
              }}
              backgroundColor="transparent"
            />
          ) : null}
        </div>
        <div className="OverridesListElement-content">
          <div className="OverridesListElement-element">
            <div className="OverridesListElement-details">
              {isNew ? (
                <div className={classNames('OverridesListElement-new', theme)}>
                  New trophic level
                </div>
              ) : null}
              {isDel ? (
                <div className={classNames('OverridesListElement-new', theme)}>
                  Deleted trophic level
                </div>
              ) : null}
              {isReorder ? (
                <div className={classNames('OverridesListElement-new', theme)}>
                  Trophic levels reordered
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </li>
    </React.Fragment>
  );
};

// const OverrideElementTrophicLevel = ({ override, theme = 'light' }) => {
//   return (
//     <div className={classNames('OverrideElement', theme)}>
//       <div className="OverrideElement-property">Property</div>
//       <div className="OverrideElement-breadcrumbs">
//         {/* {override.type !== 'new' && override.type !== 'removed' ? ( */}
//         <div className="OverrideElement-description">
//           <div className="OverrideElement-connection">Connection</div>
//           <div className="OverrideElement-fromToArrow">
//             <div className="OverrideElement-from">From</div>
//             <div className="OverrideElement-arrow">→</div>
//             <div className="OverrideElement-to">To</div>
//           </div>
//           <div className="OverrideElement-units">Units</div>
//         </div>
//         {/* ) : null} */}
//       </div>
//     </div>
//   );
// };

export { OverrideElement, OverridesListElement, OverrideElementTrophicLevel };
